import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapPin, Globe, Plane, ChevronDown, ChevronUp, Star, Calendar } from 'lucide-react';
import { FONTS } from '../../lib/theme';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';

// ---------------------------------------------------------------------------
// City → ISO 3166-1 numeric country code mapping
// Used to determine which countries to highlight based on visited cities
// ---------------------------------------------------------------------------
// Exported so the PDF cover map (and other consumers that need ISO numeric
// codes for country highlighting) can resolve city → country code without
// duplicating the table. The shared geographyConstants.CITY_TO_COUNTRY maps
// to country *names* — different shape; both are useful.
export const CITY_TO_COUNTRY = {
  // North America
  'new york': '840', 'los angeles': '840', 'san francisco': '840', 'miami': '840',
  'chicago': '840', 'las vegas': '840', 'seattle': '840', 'boston': '840',
  'washington': '840', 'honolulu': '840', 'austin': '840', 'nashville': '840',
  'new orleans': '840', 'denver': '840', 'portland': '840', 'san diego': '840',
  'toronto': '124', 'vancouver': '124', 'montreal': '124', 'quebec': '124', 'calgary': '124',
  'cancun': '484', 'mexico city': '484', 'playa del carmen': '484', 'tulum': '484',
  'oaxaca': '484', 'san miguel de allende': '484', 'puerto vallarta': '484', 'cancún': '484',
  // Caribbean & Central America
  'havana': '192', 'san juan': '630', 'nassau': '044', 'kingston': '388',
  'san jose': '188', 'panama city': '591', 'belize city': '084',
  'santo domingo': '214', 'punta cana': '214',
  // South America
  'lima': '604', 'cusco': '604', 'machu picchu': '604',
  'bogota': '170', 'cartagena': '170', 'medellin': '170',
  'buenos aires': '032', 'mendoza': '032', 'bariloche': '032',
  'rio de janeiro': '076', 'sao paulo': '076', 'salvador': '076', 'florianopolis': '076',
  'santiago': '152', 'valparaiso': '152', 'quito': '218', 'galapagos': '218',
  'la paz': '068', 'montevideo': '858',
  // Europe - UK & Ireland
  'london': '826', 'edinburgh': '826', 'manchester': '826', 'oxford': '826',
  'cambridge': '826', 'bath': '826', 'liverpool': '826', 'york': '826',
  'dublin': '372', 'galway': '372', 'cork': '372',
  // Europe - France
  'paris': '250', 'nice': '250', 'lyon': '250', 'marseille': '250',
  'bordeaux': '250', 'strasbourg': '250', 'toulouse': '250', 'provence': '250',
  // Europe - Spain & Portugal
  'barcelona': '724', 'madrid': '724', 'seville': '724', 'valencia': '724',
  'granada': '724', 'malaga': '724', 'ibiza': '724', 'san sebastian': '724', 'bilbao': '724',
  'lisbon': '620', 'porto': '620', 'faro': '620', 'algarve': '620',
  // Europe - Italy
  'rome': '380', 'florence': '380', 'venice': '380', 'milan': '380',
  'naples': '380', 'amalfi': '380', 'cinque terre': '380', 'turin': '380', 'sicily': '380',
  // Europe - Germany, Austria, Switzerland
  'berlin': '276', 'munich': '276', 'hamburg': '276', 'frankfurt': '276', 'cologne': '276',
  'vienna': '040', 'salzburg': '040', 'innsbruck': '040',
  'zurich': '756', 'geneva': '756', 'bern': '756', 'lucerne': '756', 'interlaken': '756', 'zermatt': '756',
  // Europe - Benelux
  'amsterdam': '528', 'rotterdam': '528', 'the hague': '528',
  'brussels': '056', 'bruges': '056', 'antwerp': '056', 'ghent': '056',
  'luxembourg': '442',
  // Europe - Scandinavia
  'copenhagen': '208', 'faroe islands': '208',
  'stockholm': '752', 'gothenburg': '752',
  'oslo': '578', 'bergen': '578', 'tromso': '578', 'lofoten': '578',
  'helsinki': '246', 'reykjavik': '352',
  // Europe - Eastern
  'prague': '203', 'budapest': '348', 'warsaw': '616', 'krakow': '616',
  'bucharest': '642', 'sofia': '100', 'belgrade': '688',
  'zagreb': '191', 'dubrovnik': '191', 'split': '191',
  'ljubljana': '705', 'bratislava': '703',
  'tallinn': '233', 'riga': '428', 'vilnius': '440',
  // Europe - Greece & Turkey
  'athens': '300', 'santorini': '300', 'mykonos': '300', 'crete': '300', 'rhodes': '300', 'corfu': '300',
  'istanbul': '792', 'cappadocia': '792', 'antalya': '792', 'bodrum': '792',
  // Europe - Other
  'moscow': '643', 'st petersburg': '643',
  'kyiv': '804',
  'monaco': '492',
  // Asia - East
  'tokyo': '392', 'kyoto': '392', 'osaka': '392', 'hiroshima': '392', 'nara': '392', 'hakone': '392',
  'seoul': '410', 'busan': '410', 'jeju': '410',
  'beijing': '156', 'shanghai': '156', 'hong kong': '344', 'macau': '446',
  'taipei': '158', 'kaohsiung': '158',
  // Asia - Southeast
  'bangkok': '764', 'chiang mai': '764', 'phuket': '764', 'koh samui': '764', 'krabi': '764',
  'singapore': '702',
  'kuala lumpur': '458', 'penang': '458', 'langkawi': '458',
  'bali': '360', 'jakarta': '360', 'yogyakarta': '360',
  'hanoi': '704', 'ho chi minh city': '704', 'da nang': '704', 'hoi an': '704',
  'manila': '608', 'cebu': '608', 'palawan': '608', 'el nido': '608',
  'phnom penh': '116', 'siem reap': '116', 'luang prabang': '418', 'vientiane': '418',
  'yangon': '104',
  // Asia - South
  'mumbai': '356', 'delhi': '356', 'new delhi': '356', 'jaipur': '356', 'goa': '356',
  'agra': '356', 'varanasi': '356', 'udaipur': '356', 'kerala': '356',
  'colombo': '144', 'kathmandu': '524', 'dhaka': '050',
  // Asia - Middle East
  'dubai': '784', 'abu dhabi': '784',
  'doha': '634', 'muscat': '512',
  'riyadh': '682', 'jeddah': '682',
  'amman': '400', 'petra': '400',
  'beirut': '422', 'tel aviv': '376', 'jerusalem': '376',
  // Africa
  'cairo': '818', 'luxor': '818', 'aswan': '818',
  'marrakech': '504', 'fez': '504', 'casablanca': '504', 'chefchaouen': '504',
  'cape town': '710', 'johannesburg': '710', 'kruger': '710',
  'nairobi': '404', 'mombasa': '404', 'masai mara': '404',
  'dar es salaam': '834', 'zanzibar': '834', 'serengeti': '834', 'kilimanjaro': '834',
  'accra': '288', 'lagos': '566', 'addis ababa': '231',
  'kigali': '646', 'windhoek': '516', 'victoria falls': '716',
  'tunis': '788', 'algiers': '012',
  'mauritius': '480', 'seychelles': '690', 'madagascar': '450',
  // Oceania
  'sydney': '036', 'melbourne': '036', 'brisbane': '036', 'perth': '036', 'cairns': '036',
  'gold coast': '036', 'great barrier reef': '036',
  'auckland': '554', 'queenstown': '554', 'wellington': '554', 'christchurch': '554', 'rotorua': '554',
  'fiji': '242', 'bora bora': '258', 'tahiti': '258',
};

/**
 * Country name (lowercase) → ISO 3166-1 numeric code.
 *
 * Used as a fallback in `getCountryCode` and exported for callers that
 * receive country-only strings (e.g. user adds "Russia" via the Add
 * Place form with no city). Without this fallback, country-only entries
 * never resolve to an ISO code and never highlight on the map.
 */
export const COUNTRY_NAME_TO_ISO_NUMERIC = {
  // North America
  'united states': '840', 'usa': '840', 'us': '840', 'america': '840',
  'canada': '124', 'mexico': '484',
  // Caribbean & Central America
  'cuba': '192', 'puerto rico': '630', 'jamaica': '388', 'bahamas': '044',
  'dominican republic': '214', 'panama': '591', 'belize': '084', 'costa rica': '188',
  // South America
  'argentina': '032', 'brazil': '076', 'chile': '152', 'peru': '604',
  'colombia': '170', 'ecuador': '218', 'bolivia': '068', 'uruguay': '858',
  'venezuela': '862', 'paraguay': '600', 'guyana': '328', 'suriname': '740',
  // Europe
  'united kingdom': '826', 'uk': '826', 'great britain': '826', 'britain': '826', 'england': '826',
  'ireland': '372', 'france': '250', 'spain': '724', 'portugal': '620',
  'italy': '380', 'germany': '276', 'austria': '040', 'switzerland': '756',
  'netherlands': '528', 'belgium': '056', 'luxembourg': '442',
  'denmark': '208', 'sweden': '752', 'norway': '578', 'finland': '246',
  'iceland': '352', 'czech republic': '203', 'czechia': '203',
  'hungary': '348', 'poland': '616', 'romania': '642', 'bulgaria': '100',
  'serbia': '688', 'croatia': '191', 'slovenia': '705', 'slovakia': '703',
  'estonia': '233', 'latvia': '428', 'lithuania': '440',
  'greece': '300', 'turkey': '792', 'russia': '643', 'russian federation': '643',
  'ukraine': '804', 'belarus': '112', 'monaco': '492', 'malta': '470',
  'cyprus': '196', 'albania': '008', 'bosnia and herzegovina': '070', 'montenegro': '499',
  'north macedonia': '807', 'macedonia': '807', 'moldova': '498',
  // Asia
  'japan': '392', 'south korea': '410', 'korea': '410',
  'china': '156', 'taiwan': '158', 'hong kong': '344', 'macau': '446',
  'thailand': '764', 'singapore': '702', 'malaysia': '458',
  'indonesia': '360', 'vietnam': '704', 'philippines': '608',
  'cambodia': '116', 'laos': '418', 'myanmar': '104', 'burma': '104',
  'india': '356', 'sri lanka': '144', 'nepal': '524', 'bangladesh': '050',
  'bhutan': '064', 'pakistan': '586', 'maldives': '462',
  // Middle East
  'united arab emirates': '784', 'uae': '784', 'qatar': '634',
  'oman': '512', 'saudi arabia': '682', 'jordan': '400',
  'lebanon': '422', 'israel': '376', 'iran': '364', 'iraq': '368',
  'kuwait': '414', 'bahrain': '048', 'yemen': '887', 'syria': '760',
  // Africa
  'egypt': '818', 'morocco': '504', 'south africa': '710',
  'kenya': '404', 'tanzania': '834', 'ghana': '288', 'nigeria': '566',
  'ethiopia': '231', 'rwanda': '646', 'namibia': '516', 'zimbabwe': '716',
  'tunisia': '788', 'algeria': '012', 'mauritius': '480', 'seychelles': '690',
  'madagascar': '450', 'botswana': '072', 'uganda': '800', 'mozambique': '508',
  'zambia': '894', 'malawi': '454', 'angola': '024',
  // Oceania
  'australia': '036', 'new zealand': '554', 'fiji': '242',
  'french polynesia': '258', 'tahiti': '258',
  // Antarctica
  'antarctica': '010',
};

/**
 * Try to resolve a destination string to an ISO country code.
 * Checks exact match, city-before-comma, substring matches, and finally
 * a country-name fallback. Returns ISO 3166-1 numeric (string) so the
 * world-atlas TopoJSON's `country.id` highlights correctly.
 */
export const getCountryCode = (destination) => {
  if (!destination || typeof destination !== 'string') return null;
  const lower = destination.toLowerCase().trim();

  // Direct city match
  if (CITY_TO_COUNTRY[lower]) return CITY_TO_COUNTRY[lower];

  // City before comma: "Tokyo, Japan" → "tokyo"
  const cityOnly = lower.split(',')[0].trim();
  if (CITY_TO_COUNTRY[cityOnly]) return CITY_TO_COUNTRY[cityOnly];

  // Country-name match: when the user adds a country without a city
  // (e.g. "Russia"), or the destination is a country form ("France").
  if (COUNTRY_NAME_TO_ISO_NUMERIC[lower]) return COUNTRY_NAME_TO_ISO_NUMERIC[lower];

  // Country-as-suffix match: "Some Place, Spain" → resolve to Spain
  // when the city before the comma isn't in CITY_TO_COUNTRY.
  const lastPart = (lower.split(',').pop() || '').trim();
  if (lastPart && lastPart !== lower && COUNTRY_NAME_TO_ISO_NUMERIC[lastPart]) {
    return COUNTRY_NAME_TO_ISO_NUMERIC[lastPart];
  }

  // Substring match across CITY_TO_COUNTRY (last because false-positive prone)
  for (const [city, code] of Object.entries(CITY_TO_COUNTRY)) {
    if (lower.includes(city) || city.includes(lower) || cityOnly.includes(city) || city.includes(cityOnly)) {
      return code;
    }
  }

  return null;
};

// ---------------------------------------------------------------------------
// City-to-SVG coordinate mapping for pins
// Using Natural Earth projection — coordinates computed at render time via d3
// But we keep a lat/lng lookup for cities to place pins
// ---------------------------------------------------------------------------
const CITY_LATLNG = {
  // North America
  'new york': [40.71, -74.01], 'los angeles': [34.05, -118.24], 'san francisco': [37.77, -122.42],
  'miami': [25.76, -80.19], 'chicago': [41.88, -87.63], 'las vegas': [36.17, -115.14],
  'seattle': [47.61, -122.33], 'boston': [42.36, -71.06], 'washington': [38.91, -77.04],
  'honolulu': [21.31, -157.86], 'toronto': [43.65, -79.38], 'vancouver': [49.28, -123.12],
  'montreal': [45.50, -73.57], 'cancun': [21.16, -86.85], 'cancún': [21.16, -86.85],
  'mexico city': [19.43, -99.13], 'austin': [30.27, -97.74], 'nashville': [36.16, -86.78],
  'new orleans': [29.95, -90.07], 'denver': [39.74, -104.99], 'portland': [45.52, -122.68],
  // South America
  'lima': [-12.05, -77.04], 'cusco': [-13.53, -71.97], 'bogota': [4.71, -74.07],
  'cartagena': [10.39, -75.51], 'medellin': [6.25, -75.56],
  'buenos aires': [-34.60, -58.38], 'rio de janeiro': [-22.91, -43.17],
  'sao paulo': [-23.55, -46.63], 'santiago': [-33.45, -70.67],
  // Europe
  'london': [51.51, -0.13], 'paris': [48.86, 2.35], 'rome': [41.90, 12.50],
  'barcelona': [41.39, 2.17], 'madrid': [40.42, -3.70], 'amsterdam': [52.37, 4.90],
  'berlin': [52.52, 13.41], 'vienna': [48.21, 16.37], 'prague': [50.08, 14.44],
  'budapest': [47.50, 19.04], 'lisbon': [38.72, -9.14], 'dublin': [53.35, -6.26],
  'edinburgh': [55.95, -3.19], 'zurich': [47.38, 8.54], 'copenhagen': [55.68, 12.57],
  'stockholm': [59.33, 18.07], 'oslo': [59.91, 10.75], 'helsinki': [60.17, 24.94],
  'reykjavik': [64.15, -21.94], 'athens': [37.98, 23.73], 'istanbul': [41.01, 28.98],
  'florence': [43.77, 11.25], 'venice': [45.44, 12.32], 'milan': [45.46, 9.19],
  'nice': [43.71, 7.26], 'brussels': [50.85, 4.35], 'munich': [48.14, 11.58],
  'dubrovnik': [42.65, 18.09], 'split': [43.51, 16.44], 'warsaw': [52.23, 21.01],
  'krakow': [50.06, 19.94], 'bucharest': [44.43, 26.10], 'santorini': [36.39, 25.46],
  'porto': [41.15, -8.61], 'seville': [37.39, -5.98], 'valencia': [39.47, -0.38],
  'lyon': [45.76, 4.84], 'salzburg': [47.80, 13.04], 'tallinn': [59.44, 24.75],
  'bruges': [51.21, 3.22], 'faroe islands': [62.01, -6.77],
  'moscow': [55.76, 37.62], 'granada': [37.18, -3.60],
  // Asia
  'tokyo': [35.68, 139.69], 'kyoto': [35.01, 135.77], 'osaka': [34.69, 135.50],
  'seoul': [37.57, 126.98], 'beijing': [39.90, 116.40], 'shanghai': [31.23, 121.47],
  'hong kong': [22.32, 114.17], 'taipei': [25.03, 121.57],
  'bangkok': [13.76, 100.50], 'chiang mai': [18.79, 98.98], 'phuket': [7.88, 98.39],
  'singapore': [1.35, 103.82], 'kuala lumpur': [3.14, 101.69],
  'bali': [-8.41, 115.19], 'hanoi': [21.03, 105.85], 'ho chi minh city': [10.82, 106.63],
  'mumbai': [19.08, 72.88], 'delhi': [28.61, 77.21], 'new delhi': [28.61, 77.21],
  'jaipur': [26.92, 75.79], 'dubai': [25.20, 55.27], 'abu dhabi': [24.45, 54.65],
  'doha': [25.29, 51.53], 'tel aviv': [32.09, 34.78], 'jerusalem': [31.77, 35.23],
  'amman': [31.95, 35.93], 'siem reap': [13.36, 103.86],
  // Africa
  'cairo': [30.04, 31.24], 'marrakech': [31.63, -8.01], 'cape town': [-33.92, 18.42],
  'nairobi': [-1.29, 36.82], 'zanzibar': [-6.16, 39.19], 'casablanca': [33.57, -7.59],
  'johannesburg': [-26.20, 28.05], 'addis ababa': [9.02, 38.75],
  'luxor': [25.69, 32.64], 'fez': [34.03, -5.00], 'dar es salaam': [-6.79, 39.28],
  // Oceania
  'sydney': [-33.87, 151.21], 'melbourne': [-37.81, 144.96], 'auckland': [-36.85, 174.76],
  'queenstown': [-45.03, 168.66], 'brisbane': [-27.47, 153.03], 'perth': [-31.95, 115.86],
};

/**
 * Get lat/lng for a destination string.
 */
const getLatLng = (destination) => {
  if (!destination || typeof destination !== 'string') return null;
  const lower = destination.toLowerCase().trim();
  if (CITY_LATLNG[lower]) return CITY_LATLNG[lower];
  const cityOnly = lower.split(',')[0].trim();
  if (CITY_LATLNG[cityOnly]) return CITY_LATLNG[cityOnly];
  for (const [city, coords] of Object.entries(CITY_LATLNG)) {
    if (lower.includes(city) || city.includes(lower) || cityOnly.includes(city) || city.includes(cityOnly)) {
      return coords;
    }
  }
  return null;
};

/**
 * TravelMap — Country-level shaded world map with city pins.
 * Uses d3-geo Natural Earth projection + world-atlas TopoJSON.
 */
const TravelMap = ({
  tripLogs = [],
  reviews = [],
  userName,
  isOwnProfile,
  onAddPlace,
  // Optional pre-computed Set of visited country codes. When the parent
  // provides this (e.g. from the You-page hub which also renders
  // ShareablePassport), we use it as the single source of truth so both
  // maps display matching counts. Falls back to internal computation.
  visitedCountrySet,
}) => {
  const [selectedPin, setSelectedPin] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [worldData, setWorldData] = useState(null);
  const [showAddPlace, setShowAddPlace] = useState(false);
  const [addCity, setAddCity] = useState('');
  const [addCountry, setAddCountry] = useState('');
  const svgRef = useRef(null);

  // Load world-atlas TopoJSON
  useEffect(() => {
    let cancelled = false;
    import('world-atlas/countries-110m.json').then(topology => {
      if (!cancelled) {
        const countries = feature(topology.default || topology, (topology.default || topology).objects.countries);
        setWorldData(countries);
      }
    }).catch(err => console.error('Failed to load world map data:', err));
    return () => { cancelled = true; };
  }, []);

  // d3 projection and path generator
  const { projection, pathGenerator } = useMemo(() => {
    const proj = geoNaturalEarth1()
      .scale(160)
      .translate([480, 260]);
    const pg = geoPath(proj);
    return { projection: proj, pathGenerator: pg };
  }, []);

  // Aggregate destinations. The visited-country set defers to a parent-
  // provided set (so the Passport's world map and this map agree), with a
  // fallback to deriving locally from tripLogs + reviews.
  const { mapData, visitedCountries } = useMemo(() => {
    const destinations = new Map();
    const countries = new Set();

    (tripLogs || []).forEach(trip => {
      const dests = Array.isArray(trip.destinations) ? trip.destinations : [];
      dests.forEach(dest => {
        if (typeof dest !== 'string') return;
        const latlng = getLatLng(dest);
        const countryCode = getCountryCode(dest);
        if (countryCode) countries.add(countryCode);
        if (!latlng) return;
        const key = dest.toLowerCase();
        if (!destinations.has(key)) {
          destinations.set(key, { name: dest, count: 0, latlng, trips: [], reviews: [] });
        }
        const entry = destinations.get(key);
        entry.count += 1;
        entry.trips.push(trip);
      });
    });

    (reviews || []).forEach(review => {
      const city = review.city;
      if (!city) return;
      const latlng = getLatLng(city);
      const countryCode = getCountryCode(city);
      if (countryCode) countries.add(countryCode);
      if (!latlng) return;
      const key = city.toLowerCase();
      if (!destinations.has(key)) {
        destinations.set(key, { name: city, count: 0, latlng, trips: [], reviews: [] });
      }
      destinations.get(key).reviews.push(review);
    });

    return {
      mapData: [...destinations.values()],
      // Prefer the parent-provided unified set when available
      visitedCountries: visitedCountrySet instanceof Set ? visitedCountrySet : countries,
    };
  }, [tripLogs, reviews, visitedCountrySet]);

  // Project pin coordinates
  const projectedPins = useMemo(() => {
    return mapData.map(d => {
      const [px, py] = projection([d.latlng[1], d.latlng[0]]) || [0, 0];
      return { ...d, x: px, y: py };
    });
  }, [mapData, projection]);

  // Stats
  const totalDestinations = mapData.length;
  const totalTrips = tripLogs?.length || 0;
  const totalCountries = visitedCountries.size;

  return (
    <div className="bg-white rounded-2xl shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-teal-500" />
            <h3 className="text-gray-800" style={FONTS.headingSemibold}>
              {isOwnProfile ? 'My Travel Map' : `${userName || 'Wanderer'}'s Travel Map`}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {isOwnProfile && (
              <button
                onClick={() => setShowAddPlace(!showAddPlace)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors hover:bg-teal-50"
                style={{ color: '#0d9488', border: '1px solid rgba(13,148,136,0.2)' }}
              >
                <MapPin className="w-3 h-3" /> Add Place
              </button>
            )}
            <button
              onClick={() => setShowStats(!showStats)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Add Place form */}
        {showAddPlace && (
          <div className="flex gap-2 mt-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">City</label>
              <input
                type="text"
                value={addCity}
                onChange={e => setAddCity(e.target.value)}
                placeholder="e.g. Tokyo"
                className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Country</label>
              <input
                type="text"
                value={addCountry}
                onChange={e => setAddCountry(e.target.value)}
                placeholder="e.g. Japan"
                className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400"
              />
            </div>
            <button
              onClick={() => {
                // Country-only is valid — the Travel Map is country-level,
                // so a user who knows they visited Russia but doesn't
                // remember a specific city should still be able to mark it.
                const cityV = addCity.trim();
                const countryV = addCountry.trim();
                if (cityV || countryV) {
                  onAddPlace?.({ city: cityV, country: countryV });
                  setAddCity('');
                  setAddCountry('');
                  setShowAddPlace(false);
                }
              }}
              disabled={!addCity.trim() && !addCountry.trim()}
              className="px-4 py-1.5 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-40"
              style={{ background: '#0d9488' }}
            >
              Add
            </button>
          </div>
        )}

        {showStats && (
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-teal-500" />
              <span className="text-sm text-gray-700" style={FONTS.bodyBold}>{totalDestinations}</span>
              <span className="text-xs text-gray-400" style={FONTS.body}>cities</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-teal-500" />
              <span className="text-sm text-gray-700" style={FONTS.bodyBold}>{totalCountries}</span>
              <span className="text-xs text-gray-400" style={FONTS.body}>countries</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Plane className="w-3.5 h-3.5 text-teal-500" />
              <span className="text-sm text-gray-700" style={FONTS.bodyBold}>{totalTrips}</span>
              <span className="text-xs text-gray-400" style={FONTS.body}>trips</span>
            </div>
          </div>
        )}
      </div>

      {/* Map SVG */}
      <div className="relative bg-gradient-to-b from-gray-50 to-gray-100 rounded-b-2xl overflow-hidden">
        <svg
          ref={svgRef}
          viewBox="0 0 960 520"
          className="w-full"
          style={{ minHeight: 280, display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Ocean */}
          <rect width="960" height="520" fill="#f0f9ff" rx="8" />

          {/* Country paths */}
          {worldData && worldData.features.map((country, i) => {
            const countryId = String(country.id);
            const isVisited = visitedCountries.has(countryId);
            return (
              <path
                key={`country-${i}`}
                d={pathGenerator(country) || ''}
                fill={isVisited ? '#99f6e4' : '#e5e7eb'}
                stroke={isVisited ? '#14b8a6' : '#d1d5db'}
                strokeWidth={isVisited ? '1.2' : '0.4'}
                className="transition-all duration-300"
              />
            );
          })}

          {/* Graticule-like subtle grid (optional subtle look) */}
          <path
            d={pathGenerator({ type: 'Sphere' }) || ''}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="0.5"
          />

          {/* Connection lines between destinations */}
          {projectedPins.length > 1 && projectedPins
            .filter(d => d.trips.length > 0)
            .sort((a, b) => {
              const dateA = a.trips[0]?.start_date || a.trips[0]?.created_at || '';
              const dateB = b.trips[0]?.start_date || b.trips[0]?.created_at || '';
              return dateA.localeCompare(dateB);
            })
            .map((d, i, arr) => {
              if (i === 0) return null;
              const prev = arr[i - 1];
              return (
                <line
                  key={`line-${i}`}
                  x1={prev.x}
                  y1={prev.y}
                  x2={d.x}
                  y2={d.y}
                  stroke="#99f6e4"
                  strokeWidth="2"
                  strokeDasharray="8 5"
                  opacity="0.6"
                />
              );
            })
          }

          {/* City pins */}
          {projectedPins.map((d, i) => {
            const isSelected = selectedPin === i;
            const hasReviews = d.reviews.length > 0;
            const pinSize = isSelected ? 8 : 5;
            return (
              <g key={`pin-${i}`}>
                {isSelected && (
                  <circle cx={d.x} cy={d.y} r="16" fill="none" stroke="#14b8a6" strokeWidth="2" opacity="0.3">
                    <animate attributeName="r" from="8" to="20" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle
                  cx={d.x}
                  cy={d.y}
                  r={pinSize}
                  fill={hasReviews ? '#f59e0b' : '#14b8a6'}
                  stroke="white"
                  strokeWidth="2"
                  className="cursor-pointer transition-all duration-200 hover:opacity-80"
                  onClick={() => setSelectedPin(isSelected ? null : i)}
                />
                {d.count > 1 && (
                  <text x={d.x} y={d.y + 3} textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" className="pointer-events-none">
                    {d.count}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Selected pin tooltip */}
        {selectedPin !== null && projectedPins[selectedPin] && (
          <div
            className="absolute bg-white rounded-xl shadow-lg border border-gray-100 p-3 z-10 max-w-[200px]"
            style={{
              left: `${Math.min(Math.max((projectedPins[selectedPin].x / 960) * 100, 8), 85)}%`,
              top: `${Math.min(Math.max((projectedPins[selectedPin].y / 520) * 100, 8), 75)}%`,
              transform: 'translate(-50%, -100%) translateY(-12px)',
            }}
          >
            <p className="text-sm text-gray-800 font-medium" style={FONTS.headingSemibold}>
              {projectedPins[selectedPin].name}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              {projectedPins[selectedPin].trips.length > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-teal-600" style={FONTS.body}>
                  <Plane className="w-3 h-3" />
                  {projectedPins[selectedPin].trips.length} trip{projectedPins[selectedPin].trips.length > 1 ? 's' : ''}
                </span>
              )}
              {projectedPins[selectedPin].reviews.length > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-amber-600" style={FONTS.body}>
                  <Star className="w-3 h-3" />
                  {projectedPins[selectedPin].reviews.length} review{projectedPins[selectedPin].reviews.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {projectedPins[selectedPin].trips[0]?.start_date && (
              <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1" style={FONTS.body}>
                <Calendar className="w-3 h-3" />
                {new Date(projectedPins[selectedPin].trips[0].start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Empty state */}
      {mapData.length === 0 && (
        <div className="text-center py-8 px-4">
          <Globe className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400" style={FONTS.body}>
            {isOwnProfile
              ? 'No destinations yet. Log a trip to start mapping your travels!'
              : 'No destinations mapped yet.'}
          </p>
        </div>
      )}

      {/* Visited destinations chips */}
      {mapData.length > 0 && (
        <div className="px-4 pb-4 pt-2">
          <div className="flex flex-wrap gap-1.5">
            {mapData.map((d, i) => (
              <button
                key={i}
                onClick={() => setSelectedPin(selectedPin === i ? null : i)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all ${
                  selectedPin === i
                    ? 'bg-teal-100 text-teal-700 ring-1 ring-teal-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={FONTS.body}
              >
                <MapPin className="w-3 h-3" />
                {d.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelMap;
